import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../api-client";

export interface DiscoverableClubRow {
  id: string;
  name: string;
  city: string | null;
  isLocalClub: boolean;
  discoverable: boolean;
  ownerId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  memberCount: number;
}

const LIST_KEY = ["staff", "discoverable-clubs"] as const;

export function useDiscoverableClubsQuery() {
  return useQuery<DiscoverableClubRow[]>({
    queryKey: LIST_KEY,
    queryFn: () =>
      getApiClient().get<DiscoverableClubRow[]>("/staff/discoverable-clubs"),
    staleTime: 30 * 1000,
  });
}

export function useSetDiscoverableMutation() {
  const qc = useQueryClient();
  return useMutation<
    DiscoverableClubRow,
    Error,
    { clubId: string; discoverable: boolean }
  >({
    mutationFn: ({ clubId, discoverable }) =>
      getApiClient().patch<DiscoverableClubRow>(
        `/staff/discoverable-clubs/${clubId}`,
        { discoverable },
      ),
    onMutate: async ({ clubId, discoverable }) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const previous = qc.getQueryData<DiscoverableClubRow[]>(LIST_KEY);
      if (previous) {
        qc.setQueryData<DiscoverableClubRow[]>(
          LIST_KEY,
          previous.map((row) =>
            row.id === clubId ? { ...row, discoverable } : row,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      const previous = (ctx as { previous?: DiscoverableClubRow[] } | undefined)
        ?.previous;
      if (previous) qc.setQueryData(LIST_KEY, previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}
